import {
  ItemWeightRange,
  ProductSelectionGroup,
  ProductSelectionPresetCreate,
  ProductSelectionPresetSearchResult,
  PsLangs,
} from '@brenger/api-client';
import { InputCheckbox, useForm } from '@brenger/react';
import { getWeightRangeForValue } from '@brenger/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import cn from 'classnames';
import React from 'react';
import { Translate } from 'react-localize-redux-dep-updated';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import '../../../../assets/product-selection/itemConfig.scss';
import { Button, IconCross, InputChips, InputNumber, InputText } from '../../../../brenger-shared-ui';
import { clarity } from '../../../../configs/clarity';
import { useTypedSelector } from '../../../../hooks';
import { r } from '../../../../routes';
import { CacheKey, SessionStorageKeys } from '../../../../types';
import { logError } from '../../../../utils/basics';
import { trackEvent } from '../../../../utils/eventTracking';
import { sanitizeProductGroup } from '../../../../utils/sanitizeProductGroup';
import { getActiveLanguageSettings, translate } from '../../../../utils/localization';
import { coreClient } from '../../../../utils/request';
import { getAccountHasBusinessFlow, isUserLoggedIn } from '../../../User/ducks';
import { actions } from '../../ducks';
import { ItemWithPSAndLot } from '../../interface';
import { MANUAL_IRI } from './ItemConfig';
import { ItemCount } from './ItemCount';
import { ItemImage } from './ItemImage';
import { ItemWeight } from './ItemWeight';
import { DimensionsCheck } from './modals/DimensionsCheck';

interface Props {
  productGroup?: ProductSelectionGroup | null;
  /**
   * Only present when used for updating existing item
   */
  existingItem?: ItemWithPSAndLot;
  preset?: ProductSelectionPresetSearchResult;
}

export interface PSFormData {
  title: string;
  length: number;
  width: number;
  height: number;
  count: number;
  job_image: string | undefined;
  image_ref: string | undefined;
  weight: number;
  weight_range?: ItemWeightRange;
  ps_temp: {
    fullTitle: string;
    properties: {
      size_variant: ProductSelectionGroup[];
      size_variant_choice: ProductSelectionGroup | undefined;
      material: ProductSelectionGroup[];
      material_choice: ProductSelectionGroup[];
    };
  };
}

export const ItemForm: React.FC<Props> = ({ productGroup, existingItem, preset }) => {
  const langCode = getActiveLanguageSettings().labels.full.replace('-', '_');
  const history = useHistory();
  const dispatch = useDispatch();
  const searchResult = useTypedSelector(state => state.generalTransport.product_selection.searchResult);

  const isLoggedIn = useTypedSelector(isUserLoggedIn);
  const hasBusinessFlow = useTypedSelector(getAccountHasBusinessFlow);
  const clientReference = useTypedSelector(state => state.generalTransport.choices?.client_reference || '');

  const isExistingItem = !!existingItem;
  const isManualItem = productGroup?.iri === MANUAL_IRI || existingItem?.ps_temp.isManual;
  const [sizeVariants, setSizeVariants] = React.useState<PSFormData['ps_temp']['properties']['size_variant']>([]);
  const [material, setMaterial] = React.useState<PSFormData['ps_temp']['properties']['material']>([]);
  const [showCheckModal, setShowCheckModal] = React.useState(false);
  const [weightCheck, setWeightCheck] = React.useState({ shouldCheck: false, isChecked: false });
  const [dimensionCheck, setDimensionCheck] = React.useState({
    shouldCheck: !!productGroup?.dimension_check_required,
    isChecked: false,
  });
  const [showErrors, setShowErrors] = React.useState(false);

  React.useEffect(() => {
    if (material.length) {
      clarity.setTag('PS_includes_material');
    }
  }, [material]);

  const isImageRequired = hasBusinessFlow ? false : !!productGroup?.picture_required;

  const form = useForm({
    initialState: {
      title: preset?.title || '',
      length: preset?.length || 0,
      width: preset?.width || 0,
      height: preset?.height || 0,
      count: 1,
      weight: existingItem?.weight || preset?.weight || 0,
      weight_range: existingItem?.weight_range || preset?.weight_range || undefined,
      image: null as string | null,
      image_ref: null as string | null,
      size_variant_choice: undefined as undefined | ProductSelectionGroup,
      material_choice: [] as ProductSelectionGroup[],
      update_preset: false,
    },
    validators: {
      title: val => !val,
      length: val => !val,
      width: val => !val,
      height: val => !val,
      count: val => val <= 0,
      image: val => isImageRequired && !val,
      weight_range: val => !val,
    },
  });

  const dimsDirty =
    (!preset && form.isFieldDirty('height') && form.isFieldDirty('width') && form.isFieldDirty('length')) || !!preset;
  /**
   * We show a warning to prevent users to submit items that are really small. Treshold is 1000 cm3
   */
  const dimsSizeWarn =
    (form.data.height.value || 20) * (form.data.width.value || 20) * (form.data.length.value || 20) <= 1000;

  React.useEffect(() => {
    if (existingItem) {
      trackEvent({
        eventCategory: 'PS - Edit item',
        eventAction: 'New',
      });
    } else {
      trackEvent({
        eventCategory: 'PS - Add item',
        eventAction: 'New',
      });
    }
  }, []);

  const getFormItemData = (): PSFormData => {
    return {
      title: form.data.title.value as string,
      length: form.data.length.value as number,
      width: form.data.width.value as number,
      height: form.data.height.value as number,
      count: form.data.count.value,
      job_image: form.data.image.value ? form.data.image.value : undefined,
      image_ref: form.data.image_ref.value ? form.data.image_ref.value : undefined,
      weight: form.data.weight.value,
      weight_range: form.data.weight_range.value,
      ps_temp: {
        fullTitle: getFullItemTitle({
          title: form.data.title.value as string,
          lang: langCode as PsLangs,
          materialChoice: form.data.material_choice.value,
        }),
        properties: {
          size_variant: sizeVariants.map(sanitizeProductGroup),
          size_variant_choice: form.data.size_variant_choice.value
            ? sanitizeProductGroup(form.data.size_variant_choice.value)
            : form.data.size_variant_choice.value,
          material: material.map(sanitizeProductGroup),
          material_choice: form.data.material_choice.value.map(sanitizeProductGroup),
        },
      },
    };
  };

  const formHasError = form.hasErrors || !!(sizeVariants.length && !form.data.size_variant_choice.value);

  /**
   * Submitting the form means either updating or adding an item
   */
  const submitForm = async (e): Promise<void> => {
    e.preventDefault();
    setShowErrors(formHasError);
    if (formHasError) {
      return;
    }
    if (
      (dimensionCheck.shouldCheck && !dimensionCheck.isChecked) ||
      (weightCheck.shouldCheck && !weightCheck.isChecked)
    ) {
      setShowCheckModal(true);
      return;
    }
    submitItem();
  };

  const queryClient = useQueryClient();
  const createPreset = useMutation({
    mutationFn: coreClient.users.createPsPreset,
  });
  const updatePreset = useMutation({
    mutationFn: coreClient.users.updatePsPreset,
  });
  let presetId = preset?.preset_id || existingItem?.ps_temp.preset_id;

  const isLoading = createPreset.isPending || updatePreset.isPending;

  const submitItem = async (): Promise<void> => {
    const item = getFormItemData();
    if (isLoggedIn && form.data.update_preset.value) {
      try {
        const payload: ProductSelectionPresetCreate = {
          title: item.title,
          length: item.length,
          width: item.width,
          height: item.height,
          weight: item.weight,
          weight_range: item.weight_range,
          branched_from: item.ps_temp.properties.size_variant_choice?.iri || productGroup?.iri || '',
          selected_options: item.ps_temp.properties.material_choice.map(m => m.iri),
        };
        if (!presetId) {
          createPreset.reset();
          await createPreset.mutateAsync(payload, {
            onSuccess: data => {
              presetId = data.preset_id;
              queryClient.removeQueries({ queryKey: [CacheKey.RETRIEVE_PS_PRESETS] });
            },
          });
        } else {
          updatePreset.reset();
          await updatePreset.mutateAsync(
            {
              ...payload,
              id: presetId,
            },
            {
              onSuccess: () => queryClient.removeQueries({ queryKey: [CacheKey.RETRIEVE_PS_PRESETS] }),
            }
          );
        }
      } catch (e) {
        logError(e);
      }
    }
    const { fragile, iriList } = isManualItem
      ? await getManualWeightFragileandIri(item)
      : getFragileAndIri(item, existingItem?.ps_temp.group || productGroup || null);

    if (existingItem) {
      /**
       * Dipatch an update action where old values will be overwritten
       */
      dispatch(
        actions.updateItemPs({
          item: {
            // existing item holds previous selected details
            ...existingItem,
            ...item,
            // important one to keep in sync is product_group for tracking
            product_groups: iriList,
            fragile,
            ps_temp: {
              ...existingItem.ps_temp,
              group: productGroup || null,
              ...item.ps_temp,
              preset_id: existingItem.ps_temp.preset_id || presetId,
            },
            lot_temp: existingItem.lot_temp,
          },
          // Once we are dealing with an existing item, uuid is always present
          uuid: existingItem?.ps_temp.uuid as string,
        })
      );
    } else {
      /**
       * Dispatch an add item action
       */
      dispatch(
        actions.addItemPs({
          ...item,
          product_groups: iriList,
          fragile,
          ps_temp: {
            group: productGroup || null,
            isManual: !!isManualItem,
            ...item.ps_temp,
            preset_id: presetId,
          },
          lot_temp: searchResult?.auctionLot,
        })
      );
    }
    dispatch(actions.getPrice());
    dispatch(actions.setQueryPs(null));
    // We go back in history because adding/editing should use 1 push and then replace. Going back makes us end up at /items, keeps the history logical when moving back/forward
    history.goBack();
  };

  /**
   * \\ START: KEEP THIS CODE IN THIS ORDER - Else the form will always be empty at start
   * On mount effect that handles what data should be loaded. Can either be:
   * - Rehydration of the form
   * - Load the searchresult details
   */
  React.useEffect(() => {
    if (!productGroup && !existingItem && !isManualItem) {
      // Values need to be set first, then we decide what to do
      return;
    }
    // First we check for rehydration, any changes done should be retrieved
    const rawSessionData = sessionStorage.getItem(SessionStorageKeys.PRODUCT_SELECTION_FORM);
    if (rawSessionData) {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { ps_temp, ...rest } = JSON.parse(rawSessionData) as PSFormData;
      form.set({
        ...rest,
        size_variant_choice: ps_temp.properties.size_variant_choice,
        material_choice: ps_temp.properties.material_choice,
      });
      // Update available choices
      setSizeVariants(ps_temp.properties.size_variant);
      setMaterial(ps_temp.properties.material);
    } else {
      /**
       * PROPERTIES
       * This is where size variants and material come from
       * Lookup follows this priority:
       * 1) Existing item
       * 2) Parent PSGroup - so current result is a child
       * 3) Current PSGroup - so current is a Parent
       * 4) In some very rare cases, we are dealing with a level in between (parent > CHILD > grandchild) PSgroup
       */
      // Dealing with case 1, 2 and 3
      const properties =
        existingItem?.ps_temp.properties ||
        (productGroup?.parent !== null && typeof productGroup?.parent !== 'string'
          ? productGroup?.parent.properties
          : productGroup.properties);
      // Size variants
      let sizeVars = properties?.size_variant || [];
      // Dealing with case 4
      if (!sizeVars.length) {
        sizeVars = productGroup?.properties?.size_variant || [];
      }
      // Material options are read from the first size variant.
      // ASSUMPTION: all size variants have the same material option
      // EXCEPTION: when there are no size variants, we might have options for this single size group at the group itself (so not at the parent)
      const materialVars = (sizeVars[0] ? sizeVars[0].properties : productGroup?.properties)?.material || [];
      // Detected material comes from the selected search result - to preselect we need a list of `property_identifier` values
      const detectedMaterial: string[] = [];
      if (searchResult && 'detected_material' in searchResult) {
        searchResult.detected_material?.forEach(
          dMat => dMat.property_identifier && detectedMaterial.push(dMat.property_identifier)
        );
      }
      if (preset?.selected_options) {
        preset.selected_options.forEach(presetMat => detectedMaterial.push(presetMat));
      }
      let title = existingItem?.title || preset?.title || searchResult?.auctionLot?.title;
      if (!isManualItem && !title) {
        title = productGroup?.items?.[0]?.name[langCode] || productGroup?.name[langCode];
      }
      const isCurrentProductGroupAVariant = !!sizeVars.find(variant => variant.iri === productGroup?.iri);
      form.set({
        title,
        length:
          existingItem?.length ||
          preset?.length ||
          searchResult?.auctionLot?.length ||
          productGroup?.items?.[0]?.length_suggested_cm ||
          0,
        width:
          existingItem?.width ||
          preset?.width ||
          searchResult?.auctionLot?.width ||
          productGroup?.items?.[0]?.width_suggested_cm ||
          0,
        height:
          existingItem?.height ||
          preset?.height ||
          searchResult?.auctionLot?.height ||
          productGroup?.items?.[0]?.height_suggested_cm ||
          0,
        weight: existingItem?.weight || preset?.weight || productGroup?.items?.[0]?.weight_suggested_kg || 0,
        weight_range: existingItem?.weight_range || preset?.weight_range,
        image: existingItem?.job_image || searchResult?.auctionLot?.image || null,
        count: existingItem?.count || 1,
        size_variant_choice:
          existingItem?.ps_temp?.properties.size_variant_choice ||
          (isCurrentProductGroupAVariant ? productGroup : undefined) ||
          undefined,
        material_choice:
          existingItem?.ps_temp?.properties.material_choice ||
          materialVars.filter(mvar => {
            return detectedMaterial?.some(
              dMaterial => dMaterial === mvar.property_identifier || dMaterial.includes(mvar.property_identifier || '')
            );
          }),
      });

      // Set the options, so the will end up in the UI
      setSizeVariants(sizeVars);
      setMaterial(materialVars);
    }
    return () => {
      // remove the storage on unmount
      sessionStorage.removeItem(SessionStorageKeys.PRODUCT_SELECTION_FORM);
    };
  }, [productGroup, existingItem]);
  /**
   * Storing the data in local storage so that we can retrieve it after a refresh.
   * Important note: this needs to stay below the hydration code
   */
  React.useEffect(() => {
    if (!productGroup && !existingItem && !isManualItem) {
      // Values need to be set first, then we decide what to do
      return;
    }
    sessionStorage.setItem(SessionStorageKeys.PRODUCT_SELECTION_FORM, JSON.stringify(getFormItemData()));
  }, [form.data]);
  /**
   * * // END: KEEP THIS CODE IN THIS ORDER
   */

  const prevTitleRef = React.useRef<null | string>();
  const onTitleInputChange = (title: string): void => {
    // This logic should only be applied to when the customer is editing an non-manual product
    if (!isExistingItem || isManualItem) {
      return;
    }
    let redirectToAddItem = false;
    // when user deletes all
    if (!title) {
      redirectToAddItem = true;
    } else {
      const prevTitle = prevTitleRef.current;
      // or when user copy pastes
      const sameTitle = prevTitle && (title.includes(prevTitle) || prevTitle.includes(title));
      if (prevTitle && !sameTitle) {
        redirectToAddItem = true;
      }
    }
    prevTitleRef.current = title;
    if (redirectToAddItem) {
      /**
       * This means that the user changed or deleted the name
       * which implies he/she wants to start over
       *  */
      // Deleting current item
      dispatch(actions.removeItemPs({ uuid: existingItem.ps_temp.uuid as string }));
      if (title) {
        // if we have a title (example: the user copy pasted something new/different)
        // let's help him a little and update the query
        dispatch(actions.setQueryPs(title));
      }
      // Send user back into search
      history.replace(r.generalFlow.items.add());
    }
  };
  const sizeVarUpdate = (choices: ProductSelectionGroup[]): void => {
    const variant = sizeVariants.find(v => v.iri === choices[0].iri);
    const shouldUpdateTitle = form.data.title.value && !!preset;
    if (variant) {
      if (variant.dimension_check_required) setDimensionCheck({ shouldCheck: true, isChecked: false });
      // Update the form
      form.set({
        title: shouldUpdateTitle ? variant.items?.[0]?.name[langCode] || variant.name[langCode] : form.data.title.value,
        length: variant.items?.[0]?.length_suggested_cm || 0,
        width: variant.items?.[0]?.width_suggested_cm || 0,
        height: variant.items?.[0]?.height_suggested_cm || 0,
        size_variant_choice: choices[0],
      });
    }
  };

  return (
    <>
      <div
        className={cn('ps-config', {
          'ps-config--new': !isExistingItem,
        })}
        // When exisitng item, we only want the back button to trigger
        onClick={!isExistingItem ? () => history.replace(r.generalFlow.items.add()) : undefined}
      >
        {isManualItem && !isExistingItem && (
          <div className="ps-config--manual-head">
            <div className="ps-config--manual-head--content font-bold">
              <Translate id={'request_flow.product_selection.head_manual_item'} />
            </div>
          </div>
        )}
        {isExistingItem && (
          <div className="ps-modal--section ps-config--update-head flex items-center">
            <div className="ps-config--update-head--content">
              <Translate id={'request_flow.product_selection.head_update_item'} />
            </div>
            <Button
              type="button"
              buttonStyle="transparant"
              // We go back in history because adding/editing should use 1 push and then replace. Going back makes us end up at /items, keeps the history logical when moving back/forward
              onClick={() => history.goBack()}
              extraClasses="ps-modal--back text-primary"
              style={{ margin: 0 }}
            >
              <IconCross height={'.9em'} width={'.9em'} />
            </Button>
          </div>
        )}
        <div className="ps-config--bg" onClick={e => e.stopPropagation()}>
          <form onSubmit={submitForm}>
            <div className="ps-config--content">
              <div className="ps-modal--section">
                <div className="mb-1">
                  <Translate id={'request_flow.product_selection.input_title'} />
                </div>
                <InputText
                  className="w-full"
                  value={form.data.title.value || ''}
                  onChange={title => {
                    onTitleInputChange(title);
                    form.set({
                      title,
                    });
                  }}
                />
                {form.isFieldDirty('title') && !form.data.title.value && (
                  <div className="text-[#e10000] py-1">
                    <Translate id={'request_flow.product_selection.error_title'} />
                  </div>
                )}
              </div>
              {!!sizeVariants.length && (
                <div className="ps-modal--section">
                  <InputChips
                    id="size_variant"
                    title={translate('request_flow.product_selection.questions.what_size')}
                    value={[form.data.size_variant_choice.value?.iri || '']}
                    multiple={false}
                    onChange={choices => sizeVarUpdate(sizeVariants.filter(s => choices.includes(s.iri)))}
                    options={sizeVariants.map(s => ({
                      value: s.iri,
                      label: s.label?.[langCode] as string,
                    }))}
                  />
                  {form.isFieldDirty('size_variant_choice') && !form.data.size_variant_choice.value && (
                    <div className="text-[#e10000] py-1">
                      <Translate id={'request_flow.product_selection.error_make_choice'} />
                    </div>
                  )}
                </div>
              )}
              <div className="ps-modal--section">
                <div className="mb-1">
                  <Translate id={'request_flow.product_selection.check_dimensions'} />
                </div>
                <div className="ps-config--dimensions">
                  <div className="relative">
                    <InputNumber
                      placeholder={translate('request_flow.product_selection.placeholder_length')}
                      className="ps-config--dimensions-input"
                      value={form.data.length.value || ''}
                      onChange={length => {
                        form.set({ length });
                        setDimensionCheck({ ...dimensionCheck, isChecked: true });
                      }}
                    />
                    <span className="ps-config--dimensions-input-suffix">cm</span>
                  </div>
                  <div className="relative">
                    <InputNumber
                      placeholder={translate('request_flow.product_selection.placeholder_width')}
                      className="ps-config--dimensions-input"
                      value={form.data.width.value || ''}
                      onChange={width => {
                        form.set({ width });
                        setDimensionCheck({ ...dimensionCheck, isChecked: true });
                      }}
                    />
                    <span className="ps-config--dimensions-input-suffix">cm</span>
                  </div>
                  <div className="relative">
                    <InputNumber
                      placeholder={translate('request_flow.product_selection.placeholder_height')}
                      className="ps-config--dimensions-input"
                      value={form.data.height.value || ''}
                      onChange={height => {
                        form.set({ height });
                        setDimensionCheck({ ...dimensionCheck, isChecked: true });
                      }}
                    />
                    <span className="ps-config--dimensions-input-suffix">cm</span>
                  </div>
                </div>
                {dimsDirty && (!form.data.height.value || !form.data.width.value || !form.data.length.value) && (
                  <div className="text-[#e10000] py-1" style={{ marginTop: '.5rem' }}>
                    <Translate id={'request_flow.product_selection.error_dims'} />
                  </div>
                )}
                {dimsDirty && dimsSizeWarn && (
                  <div className="text-grey-500 py-1" style={{ marginTop: '.5rem' }}>
                    <Translate id={'request_flow.product_selection.warn_small_dims'} />
                  </div>
                )}
              </div>
              {!!material.length && (
                <div className="ps-modal--section">
                  <InputChips
                    id="material"
                    title={translate('request_flow.product_selection.questions.materials')}
                    value={form.data.material_choice.value.map(m => m.property_identifier as string)}
                    multiple={true}
                    onChange={choices =>
                      form.set({
                        material_choice: material.filter(m => choices.includes(m.property_identifier as string)),
                      })
                    }
                    options={material.map(m => ({
                      value: m.property_identifier as string,
                      label: m.label?.[langCode] as string,
                    }))}
                  />
                </div>
              )}
              <div className="ps-modal--section">
                <ItemImage
                  image={form.data.image.value}
                  required={isImageRequired}
                  showError={isImageRequired && showErrors}
                  updateImage={image =>
                    form.set({
                      image: image?.['@id'],
                      image_ref: image?.content_url,
                    })
                  }
                />
              </div>
              <div className="ps-modal--section">
                <ItemCount
                  count={form.data.count.value}
                  updateCount={count =>
                    form.set({
                      count,
                    })
                  }
                />
              </div>
              <div className="ps-modal--section">
                <ItemWeight
                  setCheck={shouldCheck => setWeightCheck({ ...weightCheck, shouldCheck })}
                  value={form.data.weight_range.value ? getWeightRangeForValue(form.data.weight_range.value.min) : null}
                  onChange={({ weight, min, max }) => form.set({ weight, weight_range: { min, max } })}
                  productGroups={
                    [productGroup, form.data.size_variant_choice.value, ...form.data.material_choice.value].filter(
                      Boolean
                    ) as ProductSelectionGroup[]
                  }
                />
              </div>
              {hasBusinessFlow && (
                <div className="ps-modal--section">
                  <div className="mb-1">
                    <Translate id="request_flow.product_selection.client_reference" />
                  </div>
                  <InputText
                    value={clientReference}
                    onChange={c => dispatch(actions.setChoice({ client_reference: c }))}
                  />
                </div>
              )}
              {isLoggedIn && (
                <div className="ps-modal--section">
                  <div className="flex gap-2 items-center">
                    <InputCheckbox
                      checked={form.data.update_preset.value}
                      onChange={() => form.set({ update_preset: !form.data.update_preset.value })}
                      id="update_preset"
                    />
                    <label htmlFor="update_preset">
                      <Translate
                        id={'request_flow.product_selection.' + (presetId ? 'update_preset' : 'save_preset')}
                      />
                    </label>
                  </div>
                </div>
              )}
              <div className="ps-modal--section">
                <Button type="submit" dataQaId="ps-submit-item" disabled={isLoading} loading={isLoading}>
                  <Translate id={`request_flow.product_selection.submit_${existingItem ? 'update' : 'add'}`} />
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
      <DimensionsCheck
        weightCheck={weightCheck.shouldCheck && !weightCheck.isChecked}
        dimensionCheck={dimensionCheck.shouldCheck && !dimensionCheck.isChecked}
        isActive={showCheckModal}
        onClose={() => setShowCheckModal(false)}
        onCancel={() => {
          setShowCheckModal(false);
          setDimensionCheck({ ...dimensionCheck, isChecked: true });
          setWeightCheck({ ...weightCheck, isChecked: true });
        }}
        onSubmit={submitItem}
      />
    </>
  );
};

const getFragileAndIri = (
  fd: PSFormData,
  startGroup: ProductSelectionGroup | null
): { iriList: string[]; fragile: boolean } => {
  // Chosen material
  const material = fd.ps_temp.properties.material_choice;
  // Chosen size
  const size = fd.ps_temp.properties.size_variant_choice;
  // Fallback Product Group, which is either the size variant or the search result
  const fallbackGroup = size || startGroup;

  let fragile = !!fallbackGroup?.items?.[0]?.fragile;
  // if selected material we will try to overwrite above values
  if (material.length) {
    try {
      // If there is a size variant, then we should look up the material from that particular size
      // The presented material choices are not always the same as the values in the sizevariant
      if (size) {
        size?.properties?.material
          // to find the heaviest one
          // we loop trough the material options of the size variant (filtered first by selected).
          .filter(m =>
            Boolean(material.find(({ property_identifier }) => m.property_identifier === property_identifier))
          )
          // then update the variable if we have a heavier one
          .forEach(variant => {
            // If there is auction weight, stick to it
            if (variant?.items?.[0]?.fragile) {
              fragile = true;
            }
          });
      } else {
        // No size variant, so the operation is straight forward
        const fragilePS = material.find(m => m.items?.[0]?.fragile);
        if (fragilePS) {
          fragile = true;
        }
      }
    } catch (e) {
      logError(e);
    }
  }
  /**
   * Simple list of strings, starting with the main group, the selected size and optional material choices
   */
  const iriList = [startGroup?.iri, size?.iri, ...material.map(chosen => chosen.iri)].filter(Boolean) as string[];
  return {
    fragile,
    iriList,
  };
};

const getManualWeightFragileandIri = async (fd: PSFormData): Promise<{ fragile: boolean; iriList: string[] }> => {
  const {
    // Weight could be present when it comes from an auction
    ps_temp: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      properties: { material_choice },
    },
  } = fd;
  /**
   * Simple list of strings, starting with the main group and optional material choices
   */
  const iriList = [MANUAL_IRI, ...material_choice.map(choice => choice.iri)];
  const fragile = material_choice.some(m => m.items?.[0]?.fragile);
  return { fragile, iriList };
};

/**
 * For new product groups we have specs to we want to reflect in the titem title,
 * but need to be translated
 */
interface GetFullItemTitle {
  title: string;
  lang: PsLangs;
  materialChoice?: ProductSelectionGroup[];
}
const getFullItemTitle = ({ title, lang, materialChoice }: GetFullItemTitle): string => {
  const material = (materialChoice || [])
    .map(option => {
      return option.label?.[lang] || '';
    })
    .filter(Boolean);
  if (material.length) {
    title = `${title} (${material.join(', ')})`;
  }
  return title;
};
